import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AboutUs() {
  useEffect(() => {
    document.title = 'About Us | Seas Heating and AC';
  }, []);
  return (
    <>
      {/* Page Header */}
      <section className="bg-seas-blue text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-2">Who We Are</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight">About Us</h1>
          <p className="text-gray-300 max-w-2xl">
            Meet Seas — your neighbor, your HVAC expert, and someone who puts his name on every job.
          </p>
        </div>
      </section>

      {/* Hero image section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src="/images/aboutus-image1.png" alt="Seas Heating and AC - About us" className="min-h-[360px] w-full object-cover rounded-xl" />
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 lg:py-24 bg-gray-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest mb-2">Our Story</p>
              <h2 className="text-2xl font-bold text-seas-gray mb-6 tracking-tight">The Seas Heating and AC Story</h2>
              <div className="prose prose-lg text-gray-600 space-y-6">
                <p>
                  Hi, I'm Cesar, but everyone calls me Seas. I was born and raised in Las Vegas, and I've been here my whole life. This city raised me, and it means everything to me to serve the same community I grew up in. I'm a family man and a proud girl dad, and my family is the reason I work the way I do.
                </p>
                <p>
                  I'm not a transplant who showed up to make a quick buck. This is my home, my name is on my work, and my reputation matters to me. I've been in the HVAC trade for 19 years—I've worked in small mom-and-pop shops, large manufacturers, and even union shops. Every stop taught me something different, and all of it shaped how I run my business today.
                </p>
                <p>
                  I finally decided to open my own shop. I still wear many hats, and I like it that way. I'm a small business owner, not a big private-equity-owned company. When you call, you're talking to me. When work gets done, I stand behind it. I treat every home and business like it's my own, because that's how I'd want someone to treat my family.
                </p>
                <p>
                  This isn't just a business to me—it's my livelihood, my name, and my way of giving back to the community that made me who I am.
                </p>
              </div>
            </div>
            <div>
              <img src="/images/aboutus-image2.png" alt="Our story - Seas Heating and AC" className="min-h-[400px] h-full w-full object-cover rounded-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Values / team image section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <img src="/images/aboutus-image3.png" alt="Our values - Seas Heating and AC" className="min-h-[300px] w-full object-cover rounded-xl" />
            <div>
              <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest mb-2">Values</p>
              <h2 className="text-2xl font-bold text-seas-gray mb-4 tracking-tight">Our Values</h2>
              <p className="text-gray-600 mb-4">
                Integrity, quality, and respect for our customers and our community drive everything we do. 
                We don't upsell—we recommend what's right for your home and your budget. When we leave a job, 
                we're proud to put our name on it.
              </p>
              <p className="text-gray-600">
                Family-owned and operated means you get a real person on the line, a real technician at your door, 
                and real accountability. That's the Seas Heating and AC difference.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 lg:py-24 bg-gray-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest mb-2">Credentials</p>
          <h2 className="text-2xl font-bold text-seas-gray mb-8 tracking-tight">Credentials & Licensing</h2>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-shrink-0 w-24 h-24 bg-seas-blue rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">#</span>
              </div>
              <div>
                <h3 className="font-bold text-seas-gray text-lg mb-2">Licensed & Insured</h3>
                <p className="text-gray-600 mb-2">
                  Seas Heating and AC operates with full licensing and insurance for your protection and peace of mind.
                </p>
                <p className="text-seas-blue font-semibold">Nevada Business License #0094895</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optional badge / image strip */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest text-center mb-6">Licensing & Badges</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <img src="/images/ourwork-image1.png" alt="Seas Heating and AC credentials" className="min-h-[180px] w-full object-cover rounded-xl" />
            <img src="/images/ourwork-image2.png" alt="Seas Heating and AC licensing" className="min-h-[180px] w-full object-cover rounded-xl" />
            <img src="/images/ourwork-image3.png" alt="Seas Heating and AC - trusted HVAC" className="min-h-[180px] w-full object-cover rounded-xl" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-seas-blue text-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-4 tracking-tight">Ready to Work With Someone Who Cares?</h2>
          <p className="text-gray-300 mb-6">
            Call Seas directly. No call centers, no runaround—just honest service from a local you can trust.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-seas-gold text-white font-semibold px-8 py-3 rounded-lg hover:bg-amber-500 transition-colors"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </>
  );
}
